import {
  Component, HostListener, OnDestroy, OnInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  filter, Subject, takeUntil
} from 'rxjs';

import {
  BIO_SURVEY_LINK, EnhancedBioStore, IEnhancedBio, OPTIMIZED_DATE
} from '@sondermind/data-access/enhanced-bio';
import { PartialProviderWithId, ProviderUserService } from '@sondermind/data-access/provider-user';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { TextareaSizes } from '@sondermind/iris/text-area';
import { IComponentCanDeactivate } from '@sondermind/features/provider-facing/provider-profile';

import { ProviderUser } from '@sondermind/utilities/models-user-provider';

interface IBioForm {
  id: FormControl<number>;
  searchBio: FormControl<string>;
  bio: FormControl<string>;
}

export enum BioOptInStatus {
  OPTED_IN = 'opted-in',
  OPTED_OUT = 'opted-out'
}

@Component({
  selector: 'member-page-profile-bio',
  templateUrl: './profile-bio.component.html',
  styleUrls: [
    './profile-bio.component.scss',
    '../../styles/profile-app-shared-styles.scss'
  ],
  standalone: false
})
export class ProfileBioPage implements OnInit, OnDestroy, IComponentCanDeactivate {
  user: ProviderUser;
  form: FormGroup<IBioForm>;
  saving: boolean = false;

  /* delete these variables in MER-2231 */
  aiBio: IEnhancedBio | null = null;
  loaded: boolean = false;
  optimizedDate = OPTIMIZED_DATE;
  bioSurveyLink = BIO_SURVEY_LINK;
  optInStatus: BioOptInStatus | null = null;
  bioOptInStatus = BioOptInStatus;
  showBioInBanner: boolean = false;
  /* */

  textareaSizes = TextareaSizes;
  irisButtonContainerAlignments = IrisButtonContainerAlignments;
  irisButtonTypes = IrisButtonTypes;
  destroyed$ = new Subject<void>();
  readonly shortBioMaxSize: number = 150;

  constructor(
    private appService: SondermindApplicationService,
    private userService: ProviderUserService,
    private snackBar: MatSnackBar,
    private enhancedBioStore: EnhancedBioStore /* remove in MER-2231 */,
  ) { }

  ngOnInit(): void {
    this.appService.currentUser$.pipe(
      filter((u) => !!u),
      takeUntil(this.destroyed$)
    ).subscribe((u) => {
      this.user = u;
      if (!this.form) {
        this.loadAiBio();
        this.initializeForm();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // @HostListener allows us to also guard against browser refresh, close, etc.
  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.form?.pristine;
  }

  loadAiBio(): void {
    const currentDate = new Date();
    if (currentDate > this.optimizedDate) {
      this.loaded = true;
      return;
    }

    this.enhancedBioStore.loadActiveBioByProviderId(this.user.personaProviderId).subscribe(() => {
      this.loaded = true;
    });
  }

  useSuggestedBio(): void {
    this.saving = true;

    this.enhancedBioStore.activate(this.aiBio.id).subscribe({
      next: () => {
        this.form?.patchValue({
          bio: this.aiBio.enhancedText
        });

        this.optInStatus = BioOptInStatus.OPTED_IN;
        this.saving = false;
        this.appService.reloadCurrentUser();
      },
      error: () => {
        this.saving = false;
        this.callAiBioSnackbarError();
      }
    });
  }

  useCurrentBio(): void {
    this.saving = true;

    this.enhancedBioStore.deactivate(this.aiBio.id).subscribe({
      next: () => {
        this.optInStatus = BioOptInStatus.OPTED_OUT;
        this.saving = false;
      },
      error: () => {
        this.saving = false;
        this.callAiBioSnackbarError();
      }
    });
  }

  callAiBioSnackbarError(): void {
    this.snackBar.open(
      'Error saving. Please try again later.',
      'Dismiss', { duration: 10000, panelClass: 'toast-color' }
    );
  }

  initializeForm(): void {
    this.form = new FormGroup<IBioForm>({
      id: new FormControl(this.user?.id, [Validators.required]),
      searchBio: new FormControl(this.user?.searchBio ?? '',
        [Validators.required, Validators.maxLength(this.shortBioMaxSize)]
      ),
      bio: new FormControl(this.user?.bio ?? '', [Validators.required])
    });
  }

  onCancel(): void {
    if (this.form.pristine) return;

    this.initializeForm();
  }

  saveForm(): void {
    if (this.saving || !this.form.valid) {
      this.snackBar.open(
        'Error saving. Please check required fields on your bio.',
        'Dismiss', { duration: 30000, panelClass: 'toast-color' }
      );
      return;
    }

    this.saving = true;

    this.userService.update(<PartialProviderWithId> this.form.value).subscribe({
      next: () => {
        this.appService.reloadCurrentUser();
        this.snackBar.open('Profile saved', 'Dismiss');
        this.saving = false;
        this.form.markAsPristine();
      },
      error: () => {
        this.snackBar.open('Profile save failed, please try again', 'Dismiss');
        this.saving = false;
      }
    });
  }
}
